import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { store } from "@/lib/storage";
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Crown, LogOut, User as UserIcon, Edit3, Sparkles } from "lucide-react";
import { toast } from "sonner";
import GiftPanel from "@/components/GiftPanel";

export default function MonEspace() {
  const nav = useNavigate();
  const { user, entitlement, config, logout } = useAuth();
  const [profile, setProfile] = useState(store.getProfile());
  const [portalBusy, setPortalBusy] = useState(false);

  useEffect(() => {
    // Récupérer le profil serveur si disponible
    if (user) {
      api.get("/profile").then(({ data }) => {
        if (data.profile) {
          setProfile(data.profile);
          store.setProfile(data.profile);
        }
      }).catch(() => {});
    }
  }, [user]);

  async function openPortal() {
    if (portalBusy) return;
    setPortalBusy(true);
    try {
      const { data } = await api.post("/subscription/portal", { return_url: window.location.origin + "/mon-espace" });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Portail indisponible.");
      setPortalBusy(false);
    }
  }

  return (
    <div className="animate-fade-in pt-6 pb-6" data-testid="my-space-page">
      <h1 className="font-serif text-3xl text-[color:var(--ivoire)]">Mon espace</h1>
      {profile?.spaceName && (
        <p className="text-sm text-[color:var(--sauge-light)] font-serif italic mt-1">« {profile.spaceName} »</p>
      )}

      {/* Profil */}
      <section className="mt-8" data-testid="profile-section">
        <p className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--sauge-light)] mb-3">Profil</p>
        <div className="rounded-2xl border border-[color:var(--border)] bg-[#142235]/60 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[color:var(--sauge)]/15 flex items-center justify-center">
            <UserIcon size={16} className="text-[color:var(--sauge-light)]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[color:var(--ivoire)] truncate">{profile?.displayName || user?.email}</p>
            <p className="text-[11px] text-[color:var(--ivoire-muted)]">{user?.email}</p>
          </div>
          <button data-testid="logout-btn" onClick={() => { logout(); nav("/"); }} className="w-9 h-9 rounded-full border border-[color:var(--border)] flex items-center justify-center text-[color:var(--ivoire-muted)]">
            <LogOut size={14} />
          </button>
        </div>
      </section>

      {/* Découverte */}
      <section className="mt-8" data-testid="discovery-section">
        <p className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--sauge-light)] mb-3">Ma découverte</p>
        {profile?.completed ? (
          <div className="rounded-2xl border border-[color:var(--border)] bg-[#142235]/60 p-4">
            <div className="text-sm text-[color:var(--ivoire)]">
              Besoin prioritaire : <span className="text-[color:var(--sauge-light)]">{profile.priorityNeed || "—"}</span>
            </div>
            <div className="text-xs text-[color:var(--ivoire-muted)] mt-1">
              {profile.audience === "adult" ? "Pour moi" : profile.audience === "baby" ? "Pour bébé" : "Pour moi et bébé"} · {profile.duration} min
            </div>
            <Link data-testid="edit-discovery" to="/decouverte" className="mt-3 inline-flex items-center gap-2 text-xs text-[color:var(--sauge-light)] underline underline-offset-4">
              <Edit3 size={12} /> Modifier mes réponses
            </Link>
          </div>
        ) : (
          <Link data-testid="start-discovery" to="/decouverte" className="w-full h-12 flex items-center justify-center gap-2 rounded-xl bg-[color:var(--sauge)]/15 border border-[color:var(--sauge)]/40 text-[color:var(--sauge-light)]">
            <Sparkles size={14} /> Faire ma découverte
          </Link>
        )}
      </section>

      {/* Abonnement */}
      <section className="mt-8" data-testid="subscription-section">
        <p className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--sauge-light)] mb-3">Mon abonnement</p>
        <div className="rounded-2xl border border-[color:var(--border)] bg-[#142235]/60 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[color:var(--sauge)]/15 flex items-center justify-center">
              <Crown size={16} className="text-[color:var(--sauge-light)]" />
            </div>
            <div className="flex-1 min-w-0">
              {entitlement.premium ? (
                <>
                  <p className="text-sm text-[color:var(--ivoire)]">Premium {entitlement.plan === "yearly" ? "annuel" : "mensuel"}</p>
                  <p className="text-[11px] text-[color:var(--ivoire-muted)]">
                    {entitlement.cancel_at_period_end
                      ? `Accès jusqu'au ${entitlement.ends_at ? new Date(entitlement.ends_at).toLocaleDateString("fr-FR") : "—"}`
                      : entitlement.ends_at
                        ? `Prochaine échéance : ${new Date(entitlement.ends_at).toLocaleDateString("fr-FR")}`
                        : "Abonnement actif"}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-[color:var(--ivoire)]">Gratuit</p>
                  <p className="text-[11px] text-[color:var(--ivoire-muted)]">Une séance de découverte par thème.</p>
                </>
              )}
            </div>
          </div>
          <div className="mt-4">
            {entitlement.premium ? (
              <button data-testid="portal-btn" onClick={openPortal} disabled={portalBusy}
                className="w-full h-12 rounded-xl border border-[color:var(--border)] text-[color:var(--ivoire)] disabled:opacity-60">
                {portalBusy ? "…" : "Gérer mon abonnement"}
              </button>
            ) : (
              <Link data-testid="upgrade-link" to="/premium" className="w-full h-12 flex items-center justify-center rounded-xl bg-[color:var(--sauge)] text-[color:var(--bg-deep)] font-medium">
                Passer Premium
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Cadeau Premium 7 jours */}
      <GiftPanel />
    </div>
  );
}
